import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, Renderer2 as Renderer } from '@angular/core';
import {
  BatteryModelEnum,
  RDSAlertsSortEnum,
  RDSAlertsSortInput,
  RDSAlertsStatus,
  RDSAlertsStatusEnum,
  RDSAlertsStatusFilter,
  RDSGenerationEnum,
  RDSPaginationInput,
  RDSSortDirectionEnum,
  SystemsService,
} from 'ngx-core-services';
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import { ColorTypeEnum, ColumnConfig, ColumnTypeEnum } from 'ngx-zcs-components';
import { BehaviorSubject, combineLatest, from, Subject } from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';

import { AlertRegistryService } from './alert-registry.service';
import { AccessSupportService } from '../../communication/access-support.service';
import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { getChipColor } from '../device-registry/device-registry.component';

const getLabels = () => ({
  actions: 'Azioni',
  alertFilter: 'Filtro Allarmi',
  alertId: 'ID Allarme',
  alertIdPlaceholder: 'ID Allarme',
  alertName: 'Nome Allarme',
  alertRegistryTitle: 'Registro Allarmi',
  applyFilters: 'Applica filtri',
  batteryModel: 'Battery Model',
  batteryModelPlaceholder: 'Battery Model',
  createdAt: 'Data Creazione',
  deviceFilter: 'Filtro dispositivo',
  deviceId: 'ID Dispositivo',
  generationPlaceholder: 'Generazione Inverter',
  itemsPerPageLabel: 'Elementi per pagina',
  noData: 'Nessun dato disponibile',
  dspmPlaceholder: 'DSPM',
  dspsPlaceholder: 'DSPS',
  firmwareFilters: 'Filtri firmware',
  armPlaceholder: 'ARM',
  noResults: 'Nessun risultato trovato',
  of: 'di',
  resetFilters: 'Resetta filtri',
  search: 'Cerca',
  selectColumns: 'Seleziona colonne',
  status: 'Stato',
  statusPlaceholder: 'Seleziona Stato',
  updatedAt: 'Last Update',
});

@CreateCustomBlockSizeType({
  'breakpoint-500': 500,
})
@Component({
  selector: 'lib-alert-registry',
  templateUrl: './alert-registry.component.html',
  styleUrls: ['./alert-registry.component.scss'],
})
export class AlertRegistryComponent implements ICustomBlock, AfterViewInit {
  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.updateColumnLabels();
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  @Input() deviceId: string | undefined | null;

  batteryModelOptions = Object.values(BatteryModelEnum).map((model) => ({
    value: model,
    label: model,
  }));

  statusOptions = [
    { value: RDSAlertsStatusEnum.OPEN, label: 'Aperto' },
    { value: RDSAlertsStatusEnum.CLOSE, label: 'Chiuso' },
  ];

  generationOptions = Object.values(RDSGenerationEnum).map((generation) => ({
    value: generation,
    label: generation,
  }));

  defaultPaginationConfig = {
    pageSize: 10,
    pageSizeOptions: [10, 25, 50],
    length: 100,
  };

  paginationConfig: BehaviorSubject<typeof this.defaultPaginationConfig> = new BehaviorSubject(this.defaultPaginationConfig);

  columns: ColumnConfig[] = [];

  sort: Subject<RDSAlertsSortInput> = new Subject();
  pagination: BehaviorSubject<RDSPaginationInput> = new BehaviorSubject({ pageIndex: 0, pageSize: 10 });

  filter: Subject<RDSAlertsStatusFilter> = new Subject();
  data$ = combineLatest([
    this.pagination,
    this.sort.pipe(startWith(undefined)),
    this.filter.pipe(startWith({})),
  ]).pipe(
    switchMap(([pagination, sort, filter]) =>
      from(this.alertRegistryService.getAlerts(pagination, filter, sort)).pipe(
        tap((data) => this.paginationConfig.next({ ...this.paginationConfig.value, length: data?.totalCount || 0 })),
        startWith(null)
      )
    ),
    map((data) => data?.alerts ?? null),
  );

  filterForm!: FormGroup;

  constructor(
    private readonly accessSupportService: AccessSupportService,
    private readonly alertRegistryService: AlertRegistryService,
    private readonly cdr: ChangeDetectorRef,
    private readonly fb: FormBuilder,
    readonly el: ElementRef,
    readonly elementRef: ElementRef,
    readonly renderer: Renderer,
    readonly route: ActivatedRoute,
    readonly router: Router,
    readonly systemsService: SystemsService,
  ) {
    this.onSubmit = this.onSubmit.bind(this);
    this.resetFilter = this.resetFilter.bind(this);
  }

  ngAfterViewInit(): void {
    this.updateColumnLabels();
    this.filterForm = this.fb.group({
      alertIdByGeneration: this.fb.group({
        alertId: [null],
        generation: [null]
      }, { validators: this.alertIdByGenerationValidator }),
      deviceId: [''],
      arm: [''],
      dspm: [''],
      dsps: [''],
      batteryModel: [null],
      status: [null]
    });

    if (this.deviceId) {
      this.filterForm.patchValue({ deviceId: this.deviceId });
      this.filter.next({ deviceId: this.deviceId });
    } else {
      const savedFilters = localStorage.getItem('alertRegistryFilters');
      if (savedFilters) {
        this.filterForm.patchValue(JSON.parse(savedFilters));
        this.filter.next(JSON.parse(savedFilters));
      }
    }

    this.cdr.detectChanges();
  }

  onPageChange(event: PageEvent) {
    this.pagination.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  onSortChange(event: Sort) {
      this.pagination.next({ pageIndex: 0, pageSize: this.defaultPaginationConfig.pageSize });

      const sortColumn = event.active as keyof typeof RDSAlertsSortEnum;
      const direction = event.direction === 'asc' ? RDSSortDirectionEnum.ASC : RDSSortDirectionEnum.DESC;

      if (sortColumn in RDSAlertsSortEnum) {
        this.sort.next({
          column: RDSAlertsSortEnum[sortColumn],
          sortDirection: direction,
        });
      } else {
        this.sort.next(undefined);
      }
    }

  updateColumnLabels(): void {
    this.columns = [
      {
        columnDef: 'alertId',
        header: this.labels.alertId,
        type: ColumnTypeEnum.Text,
        cell: (row: RDSAlertsStatus) => `${row.alertId}`,
        sortable: false,
      },
      {
        columnDef: 'alertName',
        header: this.labels.alertName,
        type: ColumnTypeEnum.Text,
        cell: (row: RDSAlertsStatus) => row.alertName,
        sortable: false,
      },
      {
        columnDef: 'deviceId',
        header: this.labels.deviceId,
        type: ColumnTypeEnum.Text,
        cell: (row: RDSAlertsStatus) => row.deviceId,
        sortable: false,
      },
      {
        columnDef: 'status',
        header: this.labels.status,
        type: ColumnTypeEnum.Chip,
        cell: (row: RDSAlertsStatus) => row.status === RDSAlertsStatusEnum.OPEN ? 'Aperto' : 'Chiuso',
        chipColor: (row: RDSAlertsStatus) => row.status === RDSAlertsStatusEnum.OPEN ? ColorTypeEnum.Yellow : ColorTypeEnum.Primary,
        sortable: false,
      },
      {
        columnDef: 'batteryModel',
        type: ColumnTypeEnum.Text,
        header: this.labels.batteryModel,
        cell: (row: RDSAlertsStatus) => row.batteryModel,
        sortable: false,
      },
      {
        columnDef: 'arm',
        header: 'ARM',
        type: ColumnTypeEnum.Chip,
        cell: (row: RDSAlertsStatus) => row.arm,
        cellHidden: (row: RDSAlertsStatus) => !row.arm,
        chipColor$: (row: RDSAlertsStatus) => getChipColor(row, 'arm', this.accessSupportService),
        sortable: false,
      },
      {
        columnDef: 'dspm',
        header: 'DSPM',
        type: ColumnTypeEnum.Chip,
        cell: (row: RDSAlertsStatus) => row.dspm,
        cellHidden: (row: RDSAlertsStatus) => !row.dspm,
        chipColor$: (row: RDSAlertsStatus) => getChipColor(row, 'arm', this.accessSupportService),
        sortable: false,
      },
      {
        columnDef: 'dsps',
        header: 'DSPS',
        type: ColumnTypeEnum.Chip,
        cell: (row: RDSAlertsStatus) => row.dsps,
        cellHidden: (row: RDSAlertsStatus) => !row.dsps,
        chipColor$: (row: RDSAlertsStatus) => getChipColor(row, 'arm', this.accessSupportService),
        sortable: false,
      },
      {
        columnDef: 'createdAt',
        header: this.labels.createdAt,
        type: ColumnTypeEnum.Date,
        format: 'dd/MM/yyyy HH:mm',
        cell: (row: RDSAlertsStatus) => row.createdAt,
        sortable: true,
      },
      {
        columnDef: 'updated_at',
        header: this.labels.updatedAt,
        type: ColumnTypeEnum.Date,
        format: 'dd/MM/yyyy HH:mm',
        cell: (row: RDSAlertsStatus) => row.updatedAt,
        sortable: true,
      },
    ];
  }

  onSubmit(): void {
    if (this.filterForm.valid) {
      localStorage.setItem('alertRegistryFilters', JSON.stringify(this.filterForm.value));
      this.pagination.next({ pageIndex: 0, pageSize: this.defaultPaginationConfig.pageSize || 10 });
      const filters = this.filterForm.value;
      this.filter.next({
        deviceId: filters.deviceId,
        batteryModel: filters.batteryModel,
        status: filters.status,
        arm: filters.arm,
        dspm: filters.dspm,
        dsps: filters.dsps,
        alertIdByGeneration: {
          alertId: filters.alertIdByGeneration.alertId ? +filters.alertIdByGeneration.alertId : undefined,
          generation: filters.alertIdByGeneration.generation,
        }
      });
    }
  }

  resetFilter(): void {
    this.pagination.next({ pageIndex: 0, pageSize: this.defaultPaginationConfig.pageSize || 10 });
    this.filterForm.reset();
    localStorage.removeItem('alertRegistryFilters');
    this.filter.next({});
  }

  onFilterChange(filter: RDSAlertsStatusFilter) {
    this.pagination.next({ pageIndex: 0, pageSize: this.defaultPaginationConfig.pageSize || 10 });
    this.filter.next(filter);
  }

  alertIdByGenerationValidator(group: AbstractControl): ValidationErrors | null {
    const alertId = group.get('alertId')?.value;
    const generation = group.get('generation')?.value;
    if ((alertId && !generation) || (!alertId && generation)) {
      return { incompleteAlertIdByGeneration: true };
    }
    return null;
  }
}
